import type React from 'react';
import { forwardRef, isValidElement, useId, type HTMLAttributes } from 'react';
import { Controller } from 'react-hook-form';
import { Radio, RadioGroup } from 'react-aria-components';
import { cn, RadioIndicator, Switch } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { StyledCheckbox } from '../Common';
import {
  CountryDropdownStyle,
  Dropdown,
  type ISortedDropdownProps,
} from '../Dropdown';
import type {
  BaseMarketplacesDropdownProps,
  CheckboxFormFieldProps,
  DropdownFormFieldProps,
  LegacyDropdownProps,
  Marketplace,
  MarketplaceOption,
  RadioFormFieldProps,
} from 'types/common/form-fields';
import {
  CustomField,
  FieldLabel,
  FieldMessage,
  FormHelpLabel,
  FormHelpToolTip,
  MuteLabel,
  RequiredMark,
} from './chrome';

/**
 * `@elements/Dropdown` under its old FormField name. `imageMarginTop` and the
 * other Semantic-only props are accepted and dropped.
 */
export function StyledDropdown({
  imageMarginTop: _imageMarginTop,
  ...props
}: ISortedDropdownProps & LegacyDropdownProps) {
  /* Semantic ignored `sorted` here, so options stay in the caller's order. */
  return <Dropdown {...props} sorted={false} />;
}

/** A label that is already an element is the caller's own, drawn as is. */
function DropdownLabel({
  label,
  required,
  helpText,
  helpPosition,
}: {
  label: React.ReactNode;
  required?: boolean;
  helpText?: string;
  helpPosition?: DropdownFormFieldProps['helpPosition'];
}) {
  if (!label) {
    return null;
  }
  const formLabel = isValidElement(label) ? (
    label
  ) : (
    <MuteLabel className="sk:mb-1 sk:flex! sk:items-center">
      {label}
      {required ? <RequiredMark /> : null}
    </MuteLabel>
  );
  if (!helpText) {
    return formLabel;
  }
  return (
    <FormHelpLabel>
      {formLabel}
      <FormHelpToolTip helpText={helpText} helpPosition={helpPosition} />
    </FormHelpLabel>
  );
}

/* A plain label doubles as the placeholder; an element one does not. */
function labelText(label: unknown): string {
  return typeof label === 'string' || typeof label === 'number'
    ? String(label)
    : '';
}

function ariaName(label: unknown, placeholder?: unknown): string | undefined {
  if (typeof label === 'string' && label) {
    return label;
  }
  return typeof placeholder === 'string' && placeholder
    ? placeholder
    : undefined;
}

export function DropdownFormField({
  required,
  loading,
  options: data,
  error,
  label,
  hint,
  control,
  name,
  placeholder,
  defaultValue = null,
  width,
  dropdownChanged,
  inline,
  fieldClass,
  helpText,
  helpPosition = 'top center',
  className,
  style,
  sorted,
  toDropdownValue,
  fromDropdownValue,
  ...rest
}: DropdownFormFieldProps) {
  const options =
    sorted === false ? data : CommonUtility.sortDropdownOptions(data);

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <CustomField
          width={width}
          className={cn(fieldClass, className)}
          style={style}
        >
          <DropdownLabel
            label={label}
            required={required}
            helpText={helpText}
            helpPosition={helpPosition}
          />
          <Dropdown
            inline={inline}
            loading={loading}
            placeholder={placeholder || labelText(label)}
            label={ariaName(label, placeholder)}
            search
            selectOnBlur={false}
            selection
            data-field-value={name}
            error={!!error}
            required={required}
            options={options}
            sorted={false}
            onChange={(_e, item) => {
              const formValue = fromDropdownValue
                ? fromDropdownValue(item.value)
                : item.value;
              onChange(formValue);
              if (dropdownChanged) {
                dropdownChanged({ ...item, value: formValue });
              }
            }}
            value={toDropdownValue ? toDropdownValue(value) : value}
            {...rest}
          />
          {hint && <FieldMessage>{hint}</FieldMessage>}
        </CustomField>
      )}
    />
  );
}

/**
 * Each option's `helpText` shows as its description line in the list, which
 * reads better than a tooltip inside an option and reaches screen readers.
 */
export function DropdownWithTooltipFormField({
  required,
  loading,
  options: data,
  error,
  label,
  hint,
  control,
  name,
  placeholder,
  defaultValue,
  width,
  dropdownChanged,
  inline,
  fieldClass,
  helpText,
  helpPosition = 'top center',
  ...rest
}: DropdownFormFieldProps) {
  const options = CommonUtility.sortDropdownOptions(data).map(option => ({
    ...option,
    description: option.helpText,
  }));

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <CustomField width={width} className={fieldClass}>
          <DropdownLabel
            label={label}
            required={required}
            helpText={helpText}
            helpPosition={helpPosition}
          />
          <Dropdown
            inline={inline}
            loading={loading}
            placeholder={placeholder || labelText(label)}
            label={ariaName(label, placeholder)}
            selectOnBlur={false}
            data-field-value={name}
            selection
            error={!!error}
            options={options}
            sorted={false}
            value={value}
            onChange={(_e, item) => {
              onChange(item.value);
              if (dropdownChanged) {
                dropdownChanged(item);
              }
            }}
            {...rest}
            disabled={false}
          />
          {hint && <FieldMessage>{hint}</FieldMessage>}
        </CustomField>
      )}
    />
  );
}

export function CountryDropdownFormField({
  required,
  loading,
  options,
  error,
  label,
  hint,
  control,
  name,
  placeholder,
  defaultValue,
  width,
  dropdownChanged,
  fieldClass,
  labelError,
  ...rest
}) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value, onBlur } }) => (
        <CustomField width={width} className={fieldClass}>
          <div className="sk:flex sk:items-baseline sk:justify-between">
            <DropdownLabel label={label} required={required} />
            {labelError && (
              <span className="sk:text-xs sk:text-danger">
                {labelError?.message}
              </span>
            )}
          </div>
          <CountryDropdownStyle
            loading={loading}
            placeholder={placeholder || (isValidElement(label) ? '' : label)}
            label={ariaName(label, placeholder)}
            search
            data-field-value={name}
            selectOnBlur={false}
            selection
            error={!!error}
            options={options}
            onChange={(_e, item) => {
              onChange(item.value);
              if (dropdownChanged) {
                dropdownChanged(item);
              }
            }}
            value={value}
            onBlur={onBlur}
            {...rest}
          />
          {hint && <FieldMessage>{hint}</FieldMessage>}
        </CustomField>
      )}
    />
  );
}

/* React Aria keys radios by string; options can be numbers or booleans. */
const optionKey = (index: number) => String(index);

export function RadioFormField({
  required,
  options,
  error,
  label,
  hint,
  control,
  name,
  defaultValue,
  width,
  helpText,
  helpPosition = 'top center',
  fieldClass = '',
  ...rest
}: RadioFormFieldProps) {
  const labelId = useId();
  const isDisabled = Boolean((rest as { disabled?: boolean }).disabled);

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => {
        const selected = options.findIndex(item => item.value === value);
        return (
          <CustomField width={width} className={fieldClass}>
            <FieldLabel
              id={labelId}
              label={label}
              required={required}
              helpText={helpText}
              helpPosition={helpPosition}
            />
            <RadioGroup
              name={name}
              aria-labelledby={label ? labelId : undefined}
              aria-label={label ? undefined : name}
              orientation="horizontal"
              isDisabled={isDisabled}
              isInvalid={!!error}
              value={selected === -1 ? null : optionKey(selected)}
              onChange={key => onChange(options[Number(key)]?.value)}
              className="sk:flex sk:flex-wrap sk:items-center sk:gap-x-4 sk:gap-y-1"
            >
              {options.map((item, index) => (
                <Radio
                  key={String(item.value)}
                  value={optionKey(index)}
                  isDisabled={item.disabled}
                  data-field-value={`${name}-${item.value}`}
                  className={cn(
                    'sk:group/radio sk:inline-flex sk:items-center sk:gap-2 sk:outline-none',
                    'sk:rac-disabled:cursor-not-allowed sk:rac-disabled:opacity-50',
                    'sk:cursor-pointer',
                  )}
                >
                  {({ isSelected }) => (
                    <>
                      <RadioIndicator isSelected={isSelected} />
                      <span className="sk:text-dense sk:text-fg">
                        {item.text}
                      </span>
                    </>
                  )}
                </Radio>
              ))}
            </RadioGroup>
            {hint && <FieldMessage>{hint}</FieldMessage>}
          </CustomField>
        );
      }}
    />
  );
}

/** Two to four choices as a segmented control. */
export function RadioSelectFormField({
  required,
  options,
  error,
  label,
  hint,
  control,
  name,
  defaultValue,
  helpText,
  helpPosition = 'top center',
  fieldClass = '',
}: RadioFormFieldProps) {
  const labelId = useId();

  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => {
        const selected = options.findIndex(item => item.value === value);
        return (
          <CustomField className={fieldClass || 'sk:ml-0!'}>
            <FieldLabel
              id={labelId}
              label={label}
              required={required}
              helpText={helpText}
              helpPosition={helpPosition}
            />
            <RadioGroup
              name={name}
              aria-labelledby={label ? labelId : undefined}
              aria-label={label ? undefined : name}
              orientation="horizontal"
              isInvalid={!!error}
              value={selected === -1 ? null : optionKey(selected)}
              onChange={key => onChange(options[Number(key)]?.value)}
              className="sk:flex sk:w-full sk:rounded-full sk:bg-fill-secondary sk:p-0.5"
            >
              {options.map((item, index) => (
                <Radio
                  key={String(item.value)}
                  value={optionKey(index)}
                  className={cn(
                    'sk:flex sk:h-6 sk:flex-1 sk:cursor-pointer sk:items-center sk:justify-center sk:rounded-full sk:px-3',
                    'sk:text-dense sk:font-medium sk:text-fg-secondary sk:outline-none',
                    'sk:transition-[background-color,box-shadow] sk:duration-100',
                    'sk:hover:text-fg',
                    'sk:rac-selected:bg-surface sk:rac-selected:text-fg sk:rac-selected:shadow-card',
                    'sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-solid sk:rac-focus-visible:outline-primary',
                  )}
                >
                  {item.text}
                </Radio>
              ))}
            </RadioGroup>
            {hint && <FieldMessage>{hint}</FieldMessage>}
          </CustomField>
        );
      }}
    />
  );
}

export function CheckboxFormField({
  required,
  error: _error,
  label,
  text,
  disabled,
  hint,
  labelClass = '',
  control,
  name,
  width,
  defaultValue = null,
  fieldProps,
  fieldClass,
  helpText,
  helpPosition = 'top center',
  ...rest
}: CheckboxFormFieldProps) {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => (
        <CustomField width={width} className={fieldClass} {...fieldProps}>
          <FieldLabel
            label={label}
            required={required}
            helpText={helpText}
            helpPosition={helpPosition}
            className={labelClass}
          />
          <StyledCheckbox
            className={cn('sk:mr-2!', fieldClass)}
            name={name}
            data-field-value={name}
            checked={!!value}
            label={text}
            disabled={disabled}
            onChange={(_, { checked }) => onChange(checked)}
            {...rest}
          />
          {hint && <FieldMessage>{hint}</FieldMessage>}
        </CustomField>
      )}
    />
  );
}

type DivProps = HTMLAttributes<HTMLDivElement>;

/** One of a row of boxed checkboxes; wraps a `BoxGreenCheckboxField`. */
export const BoxGreenCheckboxWrapper = forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...rest }, ref) => (
    <div
      {...rest}
      ref={ref}
      className={cn(
        'field sk:w-full sk:px-1.5 sk:[&.alone]:max-w-fit',
        className,
      )}
    />
  ),
);
BoxGreenCheckboxWrapper.displayName = 'BoxGreenCheckboxWrapper';

/*
 * The box is drawn on the checkbox's own holder, which is where callers'
 * `className` (`no-border`, `noCheckboxSelected`) lands.
 */
const BOX_CHECKBOX = cn(
  'sk:flex! sk:min-h-10 sk:w-full sk:items-center sk:rounded-option sk:px-4 sk:py-1.5',
  'sk:inset-shadow-hairline sk:bg-fill-quaternary',
  'sk:[&.no-border]:inset-shadow-none sk:[&.no-border]:bg-transparent',
  'sk:[&.noCheckboxSelected]:ring-1 sk:[&.noCheckboxSelected]:ring-danger sk:[&.noCheckboxSelected]:bg-danger-bg',
  'sk:has-[input:checked]:bg-fill-secondary',
  'sk:has-[input:disabled]:opacity-50',
  // Callers pass their text as a `<label>`, which sits inside the kit checkbox's own `<label>`.
  'sk:[&_label_label]:mb-0! sk:[&_label_label]:flex sk:[&_label_label]:flex-col sk:[&_label_label]:justify-center',
  'sk:[&_label_label>div]:text-xs sk:[&_label_label>div]:leading-4 sk:[&_label_label>div]:text-fg-tertiary',
  'sk:[&_label_label>div:first-child]:text-dense sk:[&_label_label>div:first-child]:font-semibold sk:[&_label_label>div:first-child]:text-fg',
);

export function BoxGreenCheckboxField({
  className,
  ...props
}: CheckboxFormFieldProps) {
  return (
    <CheckboxFormField {...props} className={cn(BOX_CHECKBOX, className)} />
  );
}

type GreenRadioProps = {
  /* Semantic's toggle look; the switch is always one now. */
  toggle?: boolean;
  checked?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  onChange?: () => void;
  className?: string;
  'aria-label'?: string;
};

/** An on/off switch whose handlers fire on every flip; the caller owns `checked`. */
export function GreenRadio({
  toggle: _toggle,
  checked,
  disabled,
  onClick,
  onChange,
  className,
  'aria-label': ariaLabel = 'Toggle',
}: GreenRadioProps) {
  return (
    <Switch
      aria-label={ariaLabel}
      isSelected={!!checked}
      isDisabled={disabled}
      className={className}
      onChange={() => {
        onClick?.();
        onChange?.();
      }}
    />
  );
}

export function mapMarketplacesToOptions(
  marketplaces: Marketplace[] = [],
): MarketplaceOption[] {
  return marketplaces.map(m => {
    const label =
      m.text || m.item?.alias || m.item?.name || m.item?.type || 'Marketplace';
    const logo = m?.item?.logo_uri;

    const textOnlyContent = (
      <span key={m.value} className="text">
        {label}
      </span>
    );

    return {
      key: m.value,
      value: m.value,
      text: label,
      ...(logo
        ? {
            image: {
              src: logo,
              style: {
                width: 18,
                height: 18,
                objectFit: 'contain',
                marginRight: 8,
                marginTop: 0,
              } as React.CSSProperties,
            },
            content: textOnlyContent,
          }
        : m.flag
          ? { flag: m.flag, content: textOnlyContent }
          : { content: textOnlyContent }),
    };
  });
}

export function MarketplacesDropdown({
  marketplaces = [],
  value,
  onChange,
  placeholder = 'Select source',
  className = 'mute-dropdown',
  clearable = true,
  search = true,
  selection = true,
  loading,
  label,
  required,
  error,
  width,
  fieldClass,
  helpText,
  helpPosition = 'top center',
  control,
  name,
  returnObject = false,
  ...rest
}: BaseMarketplacesDropdownProps) {
  const options = mapMarketplacesToOptions(marketplaces);

  const normalize = (v: BaseMarketplacesDropdownProps['value']) =>
    v && typeof v === 'object' ? v.value : (v ?? undefined);

  const getSelected = (id?: string | null): Marketplace | null =>
    id ? marketplaces.find(m => m.value === id) || null : null;

  const handlePrimitiveChange = (
    e: React.SyntheticEvent<HTMLElement>,
    data: LegacyDropdownProps,
    setter?: (v: Marketplace | string | null | undefined) => void,
  ) => {
    const selected = getSelected(data.value as string);
    if (setter) {
      setter(returnObject ? selected : (selected?.value ?? undefined));
    }
    onChange?.(e, { ...data, value: selected });
  };

  const dropdownProps = {
    selection,
    search,
    clearable,
    placeholder,
    className,
    loading,
    options,
    label: ariaName(label, placeholder),
    error: !!error,
  };

  if (control && name) {
    return (
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange: setField, value: fieldValue } }) => (
          <CustomField width={width} className={fieldClass}>
            <FieldLabel
              label={label}
              required={required}
              helpText={helpText}
              helpPosition={helpPosition}
            />
            <CountryDropdownStyle
              {...dropdownProps}
              value={normalize(fieldValue)}
              onChange={(e, data) => handlePrimitiveChange(e, data, setField)}
              {...rest}
            />
          </CustomField>
        )}
      />
    );
  }

  return (
    <CountryDropdownStyle
      {...dropdownProps}
      value={normalize(value)}
      onChange={(e, data) => handlePrimitiveChange(e, data)}
      {...rest}
    />
  );
}
